import classNames from "classnames";
import { useChatContext } from "duck/ui/chat/contexts/useChatContext";
import { useTranslation } from "react-i18next";

import Button from "features/components/Button";

import { ServiceAdvice } from "./types";
import { getSeverityColorClass } from "./utils";

type DataAsString = { content: string; parsedObject?: never };
type DataAsParsedObject = { content?: never; parsedObject: ServiceAdvice };
type ServiceAdviceProps = DataAsString | DataAsParsedObject;

const ServiceAdviceMessage = (props: ServiceAdviceProps) => {
  const { isStreaming } = useChatContext();
  const { t } = useTranslation("duck");

  let serviceAdvice: ServiceAdvice;
  if (props.parsedObject) {
    serviceAdvice = props.parsedObject;
  } else {
    let parsedMessage;

    try {
      parsedMessage = JSON.parse(props.content);
    } catch {
      return null;
    }

    if (!parsedMessage?.content) {
      return null;
    }

    serviceAdvice = parsedMessage.content;
  }

  const rawRecommendations = Array.isArray(serviceAdvice?.recommendations)
    ? serviceAdvice.recommendations
    : [];
  const isIncompleteData = !Array.isArray(serviceAdvice?.recommendations);

  if (isIncompleteData && !isStreaming) {
    return <div>{t("service_advisor_error")}</div>;
  }

  const recommendations = rawRecommendations
    .filter(
      (recommendation) => recommendation && typeof recommendation === "object"
    )
    .map((recommendation) => ({
      ...recommendation,
      indicators: Array.isArray(recommendation?.indicators)
        ? recommendation.indicators.filter(
            (indicator) => indicator && typeof indicator === "object"
          )
        : [],
    }));

  return (
    <div className="text-black">
      <div className="border-b border-t border-gray-500 bg-gray-50 text-gray-600 font-bold text-lg">
        {t("service_recommendations", { count: recommendations.length })}
      </div>
      {recommendations.map((recommendation, recommendationIndex) => {
        const recommendationText =
          typeof recommendation.recommendation === "string"
            ? recommendation.recommendation
            : "";
        const severity =
          typeof recommendation.severity === "string"
            ? recommendation.severity
            : undefined;
        const suggestedActions =
          typeof recommendation.suggested_actions === "string"
            ? recommendation.suggested_actions
            : "";

        return (
          <div
            key={`${
              recommendationText || "recommendation"
            }-${recommendationIndex}`}
            className="border-t border-black py-2 mb-1"
          >
            <div className="flex items-end justify-between">
              <div className="text-gray-400">{t("recommendation_label")}</div>
              <Button
                label={t("add_to_work_order")}
                size="small"
                color="secondary"
              />
            </div>
            <div className="mb-1">{recommendationText}</div>

            <div className="text-gray-400">{t("severity_label")}</div>
            <div
              className={classNames("mb-1", getSeverityColorClass(severity))}
            >
              {severity || t("no_severity")}
            </div>
            <div className="text-gray-400 w-full">
              {t("suggested_actions_label")}
            </div>
            <div className="mb-1">{suggestedActions}</div>
            <div className="text-gray-400 w-full mb-1">
              {t("indicators_label")}
            </div>
            <div>
              {recommendation.indicators.map((indicator, indicatorIndex) => {
                const indicatorId =
                  typeof indicator?.signal_event_id === "string"
                    ? indicator.signal_event_id
                    : "";
                const indicatorCount =
                  typeof indicator?.count === "number" &&
                  Number.isFinite(indicator.count)
                    ? indicator.count
                    : null;
                const indicatorDescription =
                  typeof indicator?.description === "string"
                    ? indicator.description
                    : "";

                return (
                  <div
                    key={`${indicatorId || "indicator"}-${indicatorIndex}`}
                    className="flex bg-gray-50 p-2 rounded-lg mb-2 gap-x-2 align-center items-start"
                  >
                    <div className="font-semibold">{indicatorId}</div>
                    <div className="bg-gray-400 rounded-2xl shrink-0 px-2 text-white">
                      {indicatorCount ?? ""}
                    </div>
                    <div>{indicatorDescription}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ServiceAdviceMessage;
